import { ChevronDownIcon } from '@chakra-ui/icons';
import { Box, Input, List, ListItem } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { InputProps, Option } from 'shared/types';

const AutoCompleteSelect: React.FC<InputProps> = ({
    onChange,
    value,
    mutable,
    addOption,
    customIcon,
    clearOnSelect,
    children,
    readonly,
    ...rest
}) => {
    const [inputValue, setInputValue] = useState('');
    const [lastInputValue, setLastInputValue] = useState('');
    const filteredOptions = Array.isArray(children)
        ? children.filter((option) =>
              option.props?.children?.toString().toLowerCase().includes(inputValue.toLowerCase()),
          )
        : [];
    const [showOptions, setShowOptions] = useState(false);
    const selectElement = (option: Option) => {
        if (
            mutable &&
            addOption &&
            !filteredOptions.map((filteredOption) => filteredOption?.props?.children).includes(option.label)
        ) {
            addOption?.(option.label as string);
        }
        if (!clearOnSelect) {
            setLastInputValue(option.label || '');
            setInputValue(option.label || '');
        }
        if (onChange) onChange(option?.value as string);
        setShowOptions(false);
    };

    useEffect(() => {
        const initialOptionInputValue =
            (Array.isArray(children) &&
                (children?.find((option) => option?.props?.value === value)?.props?.children as string)) ||
            '';
        setInputValue(initialOptionInputValue);
    }, [value, children]);

    const InputIcon = customIcon || ChevronDownIcon;

    return (
        <>
            <Box
                style={{
                    display: showOptions ? 'block' : 'none',
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    zIndex: '1400',
                    width: '100%',
                    height: '100%',
                }}
                onClick={() => {
                    if (!filteredOptions.map((option) => option?.props?.children).includes(inputValue)) {
                        setInputValue(lastInputValue || '');
                    }
                    setShowOptions(false);
                }}
            ></Box>

            <Box position="relative" width="100%">
                <Input
                    value={inputValue}
                    onChange={(e) => {
                        setInputValue(e.target.value);
                        setShowOptions(true);
                    }}
                    backgroundColor="white"
                    onFocus={() => setShowOptions(true)}
                    {...rest}
                    isDisabled={readonly}
                    width="100%"
                />
                {!readonly && (
                    <InputIcon
                        position="absolute"
                        top="50%"
                        right="10px"
                        color="gray.800"
                        transform="translateY(-50%)"
                        zIndex="10"
                        backgroundColor="white"
                        fontSize={customIcon ? '15px' : '21px'}
                        onClick={() => setShowOptions(!showOptions)}
                    />
                )}

                {showOptions && (
                    <List
                        style={{
                            position: 'absolute',
                            minWidth: '100%',
                            listStyle: 'none',
                            margin: 0,
                            marginTop: '3px',
                            padding: '3px',
                            border: '1px solid #ccc',
                            borderRadius: '7px',
                            zIndex: 1500,
                            backgroundColor: 'white',
                            maxHeight: '250px',
                            overflowY: 'scroll',
                        }}
                    >
                        {filteredOptions.map((option) => (
                            <ListItem
                                key={option?.props?.value}
                                onClick={() => {
                                    selectElement({ value: option?.props?.value, label: option?.props?.children });
                                }}
                                padding="1px 10px"
                                cursor={option?.props?.disabled ? 'default' : 'pointer'}
                                _hover={
                                    option?.props?.disabled
                                        ? {}
                                        : {
                                              backgroundColor: '#2A62D8',
                                              color: 'white',
                                          }
                                }
                            >
                                <option {...option.props}>{option?.props?.children}</option>
                            </ListItem>
                        ))}

                        {mutable &&
                            inputValue.trim() !== '' &&
                            !filteredOptions.some((option) => option?.props?.children.includes(inputValue)) && (
                                <ListItem
                                    onClick={() => {
                                        selectElement({ value: inputValue, label: inputValue });
                                    }}
                                    padding="1px 10px"
                                    cursor="pointer"
                                    _hover={{
                                        backgroundColor: '#2A62D8',
                                        color: 'white',
                                    }}
                                >
                                    <option value={inputValue}>{inputValue}</option>
                                </ListItem>
                            )}
                    </List>
                )}
            </Box>
        </>
    );
};

export default AutoCompleteSelect;
